// TRIAGE-01 (audit decision 3.2 — Q1/Q2 decided 2026-06-11) — the
// Settings → Shortcuts tab body.
//
// Three sections:
//   1. "Triage keys" — the user-configurable keymap. Every binding is a
//      clickable chip: click → the chip arms ("Press a key…"), the next
//      keypress becomes the binding (capture-next-keypress pattern),
//      Escape cancels. Conflicting / reserved chords are rejected with
//      an inline explanation — no silent stealing.
//   2. "Behaviour" — the auto-advance-after-decision toggle
//      (ON by default, Q2).
//   3. "Other shortcuts" — the non-configurable bindings, listed for
//      truth (the phantom `G` row of the old static list is gone).
//
// Sizing rides the shared compact presets (`packages/ui` theme/compact —
// project convention: consume, never re-derive).
import { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { fieldLabelSx } from "@aeriosiq/ui";
import {
  TRIAGE_ACTIONS,
  TRIAGE_ACTION_LABELS,
  chordLabel,
  eventToChord,
  rebindTriageKey,
  type TriageActionId,
} from "./keymap.js";
import { useTriagePreferencesStore } from "./triagePreferencesStore.js";

/** Non-configurable bindings, rendered as a static reference list. */
const FIXED_SHORTCUTS: readonly { keys: string; description: string }[] = [
  { keys: "Ctrl + Z", description: "Undo last action" },
  { keys: "Ctrl + K", description: "Open catalogue popover" },
  { keys: "Arrow keys", description: "Previous / next data point" },
  { keys: "D", description: "Toggle draw mode" },
  { keys: "R / P / L", description: "Draw mode: rectangle / polygon / line" },
  { keys: "Esc", description: "Cancel the top-most interaction / deselect" },
];

interface ArmedSlot {
  action: TriageActionId;
  index: number;
}

export function TriageShortcutsSettings(): React.ReactElement {
  const keymap = useTriagePreferencesStore((s) => s.keymap);
  const setKeymap = useTriagePreferencesStore((s) => s.setKeymap);
  const autoAdvance = useTriagePreferencesStore((s) => s.autoAdvance);
  const setAutoAdvance = useTriagePreferencesStore((s) => s.setAutoAdvance);

  const [armed, setArmed] = useState<ArmedSlot | null>(null);
  const [error, setError] = useState<string | null>(null);

  // While a slot is armed, capture the next keypress at the document
  // level (capture phase so the triage layer / dialog handlers never see
  // it) and turn it into the new binding.
  useEffect(() => {
    if (!armed) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === "Escape") {
        setArmed(null);
        setError(null);
        return;
      }
      const chord = eventToChord(e);
      if (chord === null) {
        // Bare modifier — keep waiting. Ctrl/Cmd/Alt chords are not
        // bindable (reserved for the browser + undo/redo).
        if (
          e.key === "Shift" ||
          e.key === "Control" ||
          e.key === "Alt" ||
          e.key === "Meta"
        ) {
          return;
        }
        setError("That key combination cannot be bound.");
        setArmed(null);
        return;
      }
      const result = rebindTriageKey(keymap, armed.action, armed.index, chord);
      if (!result.ok) {
        setError(
          result.reason === "reserved"
            ? `"${chordLabel(chord)}" is reserved and cannot be rebound.`
            : `"${chordLabel(chord)}" is already bound to "${TRIAGE_ACTION_LABELS[result.conflictsWith]}".`,
        );
        setArmed(null);
        return;
      }
      setKeymap(result.keymap);
      setError(null);
      setArmed(null);
    };
    document.addEventListener("keydown", onKeyDown, { capture: true });
    return () => {
      document.removeEventListener("keydown", onKeyDown, { capture: true });
    };
  }, [armed, keymap, setKeymap]);

  return (
    <>
      <Typography
        component="h2"
        variant="subtitle2"
        sx={{
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: "text.secondary",
          fontWeight: 600,
          mt: 2,
          mb: 0.5,
        }}
      >
        Triage keys
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Click a key to rebind it — press the new key, or Esc to cancel.
      </Typography>
      {error ? (
        <Typography
          variant="body2"
          color="error"
          role="alert"
          data-testid="triage-keymap-error"
          sx={{ mb: 1 }}
        >
          {error}
        </Typography>
      ) : null}
      <Box component="dl" sx={{ m: 0 }} data-testid="triage-keymap-list">
        {TRIAGE_ACTIONS.map((action) => (
          <Stack
            key={action}
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ py: 0.5 }}
          >
            <Stack
              component="dt"
              direction="row"
              spacing={0.5}
              sx={{ minWidth: 180 }}
            >
              {keymap[action].map((chord, index) => {
                const isArmed =
                  armed?.action === action && armed.index === index;
                return (
                  <Chip
                    key={`${chord}-${String(index)}`}
                    label={isArmed ? "Press a key…" : chordLabel(chord)}
                    size="small"
                    variant={isArmed ? "filled" : "outlined"}
                    color={isArmed ? "primary" : "default"}
                    onClick={() => {
                      setError(null);
                      setArmed({ action, index });
                    }}
                    data-testid={`triage-binding-${action}-${String(index)}`}
                    sx={{
                      fontFamily: "monospace",
                      fontSize: 12,
                      height: 24,
                    }}
                  />
                );
              })}
            </Stack>
            <Typography
              component="dd"
              variant="body2"
              color="text.secondary"
              sx={{ m: 0 }}
            >
              {TRIAGE_ACTION_LABELS[action]}
            </Typography>
          </Stack>
        ))}
      </Box>

      <Typography
        component="h2"
        variant="subtitle2"
        sx={{
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: "text.secondary",
          fontWeight: 600,
          mt: 2,
          mb: 0.5,
        }}
      >
        Behaviour
      </Typography>
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={autoAdvance}
            onChange={(e) => {
              setAutoAdvance(e.target.checked);
            }}
            inputProps={{ "aria-label": "Auto-advance after decision" }}
            data-testid="triage-auto-advance-toggle"
          />
        }
        label="Auto-advance after decision"
        slotProps={{ typography: { sx: { fontSize: 13 } } }}
      />
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ ...fieldLabelSx, display: "block", mb: 1 }}
      >
        After accepting or dismissing a finding, select the next pending finding
        — and jump to the next relevant image when the current one is done.
      </Typography>

      <Typography
        component="h2"
        variant="subtitle2"
        sx={{
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: "text.secondary",
          fontWeight: 600,
          mt: 2,
          mb: 0.5,
        }}
      >
        Other shortcuts
      </Typography>
      <Box component="dl" sx={{ m: 0 }}>
        {FIXED_SHORTCUTS.map((s) => (
          <Stack key={s.keys} direction="row" spacing={2} sx={{ py: 0.25 }}>
            <Typography
              component="dt"
              variant="body2"
              sx={{
                minWidth: 120,
                fontFamily: "monospace",
                color: "text.primary",
              }}
            >
              {s.keys}
            </Typography>
            <Typography
              component="dd"
              variant="body2"
              color="text.secondary"
              sx={{ m: 0 }}
            >
              {s.description}
            </Typography>
          </Stack>
        ))}
      </Box>
    </>
  );
}
